// frontend/src/components/TitleSuggestionChip.js
// GPT-5 Phase 2: AI Title Suggestions Component
// Tasteful, non-intrusive suggestion UI with Apply/Dismiss

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Button,
  Typography,
  Fade,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  AutoAwesome as SparkleIcon,
  Check as ApplyIcon,
  Close as DismissIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/axiosConfig';

const TitleSuggestionChip = ({ 
  content, 
  contentType = 'note', // 'note' or 'voice'
  currentTitle = '',
  onApply,
  onDismiss,
  isLocked = false,
  className,
  sx = {}
}) => {
  const { currentUser } = useAuth();
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Generate content hash for caching
  const generateContentHash = (text) => {
    let hash = 0;
    if (text.length === 0) return hash.toString();
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString();
  };

  // Content windowing for budget cap (~2-3k chars)
  const processContent = (rawContent) => {
    if (!rawContent) return '';
    
    const MAX_CHARS = 2500;
    const content = rawContent.replace(/<[^>]*>/g, '').trim(); // Strip HTML
    
    if (content.length <= MAX_CHARS) return content;
    
    // Head/middle/tail windowing
    const headSize = Math.floor(MAX_CHARS * 0.5);
    const tailSize = Math.floor(MAX_CHARS * 0.3);
    const middleSize = MAX_CHARS - headSize - tailSize;
    
    const head = content.substring(0, headSize);
    const tail = content.substring(content.length - tailSize);
    const middleStart = Math.floor((content.length - middleSize) / 2);
    const middle = content.substring(middleStart, middleStart + middleSize);
    
    return `${head}...${middle}...${tail}`;
  };

  // Check user preferences for AI features
  const checkUserPreferences = async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/preferences/ai', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const preferences = response.data.aiPreferences || {};
      
      // Check if AI titles are enabled for this content type
      if (contentType === 'voice' && !preferences.aiTitleVoice) {
        return false;
      }
      if (contentType === 'note' && !preferences.aiTitleNotes) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Failed to check user preferences:', error);
      return false; // Default to disabled if can't check
    }
  };

  // Fetch title suggestion
  const fetchSuggestion = async (forceRefresh = false) => {
    if (!content || isLocked || dismissed) return;
    if (currentTitle && currentTitle.trim() && !forceRefresh) return; // Don't suggest if title exists
    
    // Check user preferences first
    const aiEnabled = await checkUserPreferences();
    if (!aiEnabled) {
      return; // Silently don't show suggestions if disabled
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const processedContent = processContent(content);
      const contentHash = generateContentHash(processedContent);
      
      const token = await currentUser.getIdToken();
      const response = await axios.post('/api/ai/suggest-title', {
        kind: contentType,
        content: processedContent,
        contentHash,
        forceRefresh
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.suggestion) {
        setSuggestion({
          title: response.data.suggestion,
          confidence: response.data.confidence || 0.8
        });
        setVisible(true);
      }
    } catch (err) {
      console.error('Failed to fetch title suggestion:', err);
      
      // Handle rate limiting gracefully
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else if (err.response?.status >= 500) {
        setError('AI service temporarily unavailable');
      } else {
        setError('Failed to generate suggestion');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch suggestion on content change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestion();
    }, 500); // Small delay to avoid too many requests
    
    return () => clearTimeout(timer);
  }, [content, contentType, isLocked, dismissed]);

  const handleApply = () => {
    if (suggestion?.title) {
      onApply?.(suggestion.title);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    onDismiss?.();
  };

  const handleRefresh = () => {
    setSuggestion(null);
    fetchSuggestion(true);
  };

  // Don't render if locked, dismissed, or no content
  if (isLocked || dismissed || !content) return null;

  // Loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          my: 1,
          ...sx 
        }}
        className={className}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Generating title suggestion...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          my: 1,
          ...sx 
        }}
        className={className}
      >
        <Typography variant="body2" color="error.main" sx={{ fontSize: '0.875rem' }}>
          {error}
        </Typography>
        <Tooltip title="Try again">
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Main suggestion UI
  return (
    <Fade in={visible && !!suggestion} timeout={300}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          my: 1, 
          p: 1.5,
          bgcolor: 'primary.50',
          border: 1,
          borderColor: 'primary.200',
          borderRadius: 2,
          ...sx 
        }}
        className={className}
      >
        {/* Sparkle icon */}
        <SparkleIcon 
          sx={{ 
            color: 'primary.main', 
            fontSize: 18,
            animation: 'pulse 2s infinite'
          }} 
        />
        
        {/* Suggestion text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <span style={{ color: 'rgb(99, 102, 241)' }}>AI suggested:</span>
            <span 
              style={{ 
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              "{suggestion?.title}"
            </span>
            {suggestion?.confidence && suggestion.confidence < 0.7 && (
              <Tooltip title="Low confidence suggestion">
                <Typography 
                  component="span" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    color: 'warning.main',
                    ml: 0.5
                  }}
                >
                  ?
                </Typography>
              </Tooltip>
            )}
          </Typography>
        </Box>
        
        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<ApplyIcon />}
            onClick={handleApply}
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Apply
          </Button>
          
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<DismissIcon />}
            onClick={handleDismiss}
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              textTransform: 'none'
            }}
          >
            Dismiss
          </Button>
          
          <Tooltip title="Generate new suggestion">
            <IconButton 
              size="small" 
              onClick={handleRefresh}
              sx={{ ml: 0.5 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Fade>
  );
};

export default TitleSuggestionChip;